/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { PayloadAction } from "@reduxjs/toolkit";
import { AuthenticationTypeV3 } from "@tivocorp/data-domain-lib";
import type { AuthConfigurationV3 } from "@tivocorp/data-domain-lib";

import type { SignInFocusedField, SignInState } from "../signIn.types";

/**
 * The top-to-bottom focus order for the active tab. The DBS screen has a
 * single actionable control, the credential screen has the input column.
 */
function getVerticalFocusOrder(activeTab: string): SignInFocusedField[] {
  return activeTab === AuthenticationTypeV3.DbsLicensePlateBinding
    ? ["tab", "getNewCode"]
    : ["tab", "userId", "password", "signIn", "needHelp"];
}

/**
 * passwordToggle occupies the same vertical row as password, so vertical
 * movement treats the two as one position.
 */
function toVerticalPosition(field: SignInFocusedField): SignInFocusedField {
  return field === "passwordToggle" ? "password" : field;
}

/**
 * Moves the tab selection one step in `direction` and parks focus back on
 * the tab row. Does nothing at either end of the tab strip.
 */
function stepTab(state: SignInState, direction: number): void {
  const currentIdx: number = state.authConfigurations.findIndex(
    (c: AuthConfigurationV3): boolean =>
      c.authenticationType === state.activeTab
  );
  const nextIdx: number = currentIdx + direction;
  if (nextIdx < 0 || nextIdx > state.authConfigurations.length - 1) {
    return;
  }
  state.activeTab = state.authConfigurations[nextIdx].authenticationType;
  state.focusedField = "tab";
}

/**
 * Case reducers for D-pad navigation across the sign-in screen.
 */
export const focusNavigationReducers = {
  setFocusedField(
    state: SignInState,
    action: PayloadAction<SignInFocusedField>
  ): void {
    state.focusedField = action.payload;
  },
  focusNext(state: SignInState): void {
    const verticalOrder: SignInFocusedField[] = getVerticalFocusOrder(
      state.activeTab
    );
    const current: SignInFocusedField = toVerticalPosition(state.focusedField);
    const idx: number = verticalOrder.indexOf(current);
    if (idx >= 0 && idx < verticalOrder.length - 1) {
      state.focusedField = verticalOrder[idx + 1];
    }
  },
  focusPrev(state: SignInState): void {
    const verticalOrder: SignInFocusedField[] = getVerticalFocusOrder(
      state.activeTab
    );
    const current: SignInFocusedField = toVerticalPosition(state.focusedField);
    const idx: number = verticalOrder.indexOf(current);
    if (idx > 0) {
      state.focusedField = verticalOrder[idx - 1];
    }
  },
  switchTabLeft(state: SignInState): void {
    if (state.focusedField === "passwordToggle") {
      state.focusedField = "password";
      return;
    }
    if (state.authConfigurations.length < 2) {
      return;
    }
    if (state.focusedField === "tab") {
      stepTab(state, -1);
    }
  },
  switchTabRight(state: SignInState): void {
    if (state.focusedField === "password") {
      state.focusedField = "passwordToggle";
      return;
    }
    if (state.authConfigurations.length < 2) {
      return;
    }
    if (state.focusedField === "tab") {
      stepTab(state, 1);
    }
  }
};
